import { FIELD_FORMAT } from '../constants/field-format'
import type {
  CalculationBlockLibraryEntry,
  CalculationSlotSource,
} from '../dto/catalogue/calculation.res'
import type { CalculationBlockType, CatalogueKind } from '@prisma/client'

import { formatNumber } from './format-number'

/**
 * A library block's own parameters, as a row that copies it states them.
 *
 * Read by the dialog that inserts a copy and by the service that detaches
 * one: written out per side, a parameter added to a block had to be added to
 * two literals in two packages, and a copy quietly stopped charging what it
 * was copied from.
 *
 * Takes the fields and nothing else, so a row off the database satisfies it
 * as readily as a library entry off the wire. Here rather than beside the type
 * it is derived from, because the DTO barrel carries `@nestjs/swagger`-
 * decorated classes that throw in a browser — a value both sides read has to
 * come from this one.
 */
export type CalculationBlockFields = Pick<
  CalculationBlockLibraryEntry,
  'label' | 'type' | 'role' | 'amount' | 'code' | 'percent'
>

export const blockFieldsOf = (
  block: CalculationBlockFields,
): CalculationBlockFields => ({
  label: block.label,
  type: block.type,
  role: block.role,
  amount: block.amount,
  code: block.code,
  percent: block.percent,
})

/**
 * Anything that states how many of something it takes — a stage row, a member
 * of a Preisgruppe, the dto that writes either.
 *
 * One type rather than the pair written out per signature: the engine, the
 * line a screen prints and the two write paths all read the same two columns,
 * and a third way of stating a count has one declaration to pass through.
 */
export type Counted = {
  quantity?: number | null
  quantityCode?: string | null
}

/**
 * Whether a count means anything for a term of this kind.
 *
 * A mark-up takes a percentage of everything before it — there is nothing for
 * a count to multiply, and a stored one would be a figure the engine applies
 * and no screen shows. Read by the engine, by the line a row prints and by the
 * dialogs that offer the field, so the four cannot disagree.
 *
 * Null is not a mark-up: a row that holds several terms, or none yet, counts
 * what it charges like anything else.
 */
export const termTakesCount = (
  type: CalculationBlockType | null | undefined,
): boolean => type !== 'MARK_UP'

/**
 * How many decimals a count is quoted to — the `rowCount` field's own, read
 * off the registry so the field that types one, the line that prints it and a
 * PDF that renders it cannot disagree.
 */
export const COUNT_DECIMALS = FIELD_FORMAT.rowCount?.decimalPlaces ?? 0

/**
 * A count as a line writes it, or null where it takes one of the thing.
 *
 * Null rather than "1" because a count of one is not worth printing — the
 * position reads `Parkett`, not `1 × Parkett`.
 */
/** A count as a figure, to the precision the field declares — unlike
 *  `countLabel` it writes a count of one, which a step line states. */
export const countFigure = (quantity: number): string =>
  formatNumber(quantity, { maximumFractionDigits: COUNT_DECIMALS })

export function countLabel(held: Counted): string | null {
  if (held.quantityCode) return held.quantityCode
  if (held.quantity == null || held.quantity === 1) return null
  return formatNumber(held.quantity, { maximumFractionDigits: COUNT_DECIMALS })
}

/**
 * Two counts applied one after the other, as the one count a single row can
 * carry.
 *
 * The runtime half of this is `resolveStagedPrice`, which multiplies a row's
 * count by the term's own while the Preisgruppe is still one row. Where that
 * row is split — "Lösen & anpassen" — the product has to be written down
 * instead, and the two have to agree: a count of one drops out either way, two
 * numbers become a number, and anything the project decides becomes the
 * expression that multiplies what each of them said.
 */
export function composeCounts(a: Counted, b: Counted): Counted {
  if (!a.quantityCode && !b.quantityCode) {
    const both = (a.quantity ?? 1) * (b.quantity ?? 1)
    return { quantity: both === 1 ? null : both, quantityCode: null }
  }
  const factors = [a, b]
    .map(held =>
      held.quantityCode
        ? `(${held.quantityCode})`
        : (held.quantity ?? 1) === 1
          ? null
          : String(held.quantity),
    )
    .filter((factor): factor is string => factor != null)
  return { quantity: null, quantityCode: factors.join(' * ') }
}

/** The columns a row's source is read off — what it points at, how many
 *  requirement rows it covers, and whether it states terms of its own. */
type SlotSourceColumns = {
  blockId?: number | null
  groupId?: number | null
  productId?: number | null
  labourItemId?: number | null
  variantCount?: number
  type?: CalculationBlockType | null
}

/** The columns a row records a reference in — one of them, the rest null. */
export type SlotIdColumn = 'blockId' | 'groupId' | 'productId' | 'labourItemId'

/** What each source is, as every reader of a row has to know it. */
type SlotSourceFacts = {
  /** The column the row records it in, or null where it records no id: a
   *  budget covers requirement rows, and its own terms are columns of the
   *  row. */
  column: SlotIdColumn | null
  /** The branch of the catalogue the entry is picked out of. */
  branch: CatalogueKind | null
  /** What the row charges and what it is called are the catalogue's, so only
   *  its count is the row's own and a write naming the terms is refused. */
  followsCatalogue: boolean
  /** It can be turned into terms of the row's own — a Preisbaustein into a
   *  copy, a Preisgruppe into one row per member. A product, an
   *  Arbeitsleistung and a budget stay references: a copy of one would be a
   *  figure that stops following the price it was taken from. */
  detachable: boolean
}

/**
 * The six things a row can charge, and what each of them means for a reader.
 *
 * One declaration for four questions that were each answered per reader: which
 * column records it, which branch it is picked from, whether its terms are the
 * catalogue's, and whether it can be detached. Asked separately, they came out
 * differently — a row the dialog called a product was one the server called a
 * budget — and a seventh source would have been a silent gap in five files
 * rather than a build error here.
 *
 * Key order is the precedence `slotSourceOf` reads: a row naming two ids is
 * refused on the way in, so the order only decides what a malformed row reads
 * as.
 */
export const SLOT_SOURCE = {
  BLOCK: {
    column: 'blockId',
    branch: 'PRICE_BLOCK',
    followsCatalogue: true,
    detachable: true,
  },
  GROUP: {
    column: 'groupId',
    branch: 'PRICE_GROUP',
    followsCatalogue: true,
    detachable: true,
  },
  PRODUCT: {
    column: 'productId',
    branch: 'PRODUCT',
    followsCatalogue: true,
    detachable: false,
  },
  LABOUR: {
    column: 'labourItemId',
    branch: 'LABOUR',
    followsCatalogue: true,
    detachable: false,
  },
  BUDGET: {
    column: null,
    branch: null,
    followsCatalogue: false,
    detachable: false,
  },
  LOCAL: {
    column: null,
    branch: null,
    followsCatalogue: false,
    detachable: false,
  },
} as const satisfies Record<CalculationSlotSource, SlotSourceFacts>

/** The branch each reference is picked out of — the kinds a price source
 *  picker offers, derived so it cannot name one the table does not. */
export type ReferenceBranch = NonNullable<
  (typeof SLOT_SOURCE)[CalculationSlotSource]['branch']
>

/** The branches a row may charge out of, in the table's own order — what a
 *  picker offers, so a fifth cannot be declared and quietly not offered. */
export const REFERENCE_BRANCHES = (
  Object.keys(SLOT_SOURCE) as CalculationSlotSource[]
).flatMap(source => {
  const branch = SLOT_SOURCE[source].branch
  return branch ? [branch] : []
})

/**
 * The sources picked out of a branch of the catalogue, each with the column it
 * records, in the order the table lists them.
 *
 * What a picker offers, what an id column can hold, and what a write reads a
 * row's source off — the four were written out once per reader, and a fifth
 * would have been declared in the table and quietly offered by none of them.
 */
export const REFERENCE_SOURCES = (
  Object.keys(SLOT_SOURCE) as CalculationSlotSource[]
).flatMap(source => {
  const { column } = SLOT_SOURCE[source]
  return column ? [{ source, column }] : []
})

/**
 * Which of the six things a row charges.
 *
 * One precedence, read by the mapper that answers a screen and by the writes
 * that refuse to change what a row is not allowed to change — asked twice, the
 * two came out in a different order, and a row the dialog called a product was
 * one the server called a budget.
 *
 * A row that points at nothing, covers nothing and states nothing is `LOCAL`
 * with no terms yet: not a state anything writes, and not one worth a refusal.
 */
export function slotSourceOf(row: SlotSourceColumns): CalculationSlotSource {
  for (const { source, column } of REFERENCE_SOURCES) {
    if (row[column] != null) return source
  }
  if ((row.variantCount ?? 0) > 0) return 'BUDGET'
  return 'LOCAL'
}

/**
 * The expression a term charges by, where it is written as one.
 *
 * Read by the panel that walks a price and by the one that says why there is
 * none: both ask "which of this term's two halves is an expression", and asked
 * separately they would name different halves of the same row the day the
 * engine's precedence moves.
 */
export const termCode = (
  block: Pick<CalculationBlockFields, 'type' | 'code'>,
): string | null => (block.type === 'FORMULA' ? (block.code ?? null) : null)

/**
 * How often a term is taken, as one count — the row's and the term's own
 * composed the way the engine multiplies them.
 *
 * `composeCounts` is that composition; reading one of the two instead would
 * put an expression on the screen that does not produce the figure beside it.
 * A term that counts nothing takes neither.
 */
export const termCount = (
  block: Counted & Pick<CalculationBlockFields, 'type'>,
  row: Counted | null | undefined,
): Counted =>
  termTakesCount(block.type)
    ? composeCounts(row ?? {}, block)
    : { quantity: null, quantityCode: null }

/**
 * Which half of a term produced nothing, as an expression.
 *
 * The engine stops at the first of the two it cannot work out and says which;
 * this reads the expression of that half. A count may be stated by the row or
 * by the term, and the engine multiplies both, so either can be the one.
 */
export const failingCode = (
  block: Counted & Pick<CalculationBlockFields, 'type' | 'code'>,
  row: Counted | null | undefined,
  cause: 'term' | 'count',
): string | null =>
  cause === 'term'
    ? termCode(block)
    : (block.quantityCode ?? row?.quantityCode ?? null)

/** Whether the row's terms and name are the catalogue's rather than its own. */
export const followsCatalogue = (source: CalculationSlotSource): boolean =>
  SLOT_SOURCE[source].followsCatalogue

/**
 * The branch the row's entry is picked out of.
 *
 * Null where it charges something the catalogue does not file — a budget,
 * terms of its own — and where the tree says nothing of where a term came
 * from, which a customer-facing one does not.
 */
export const sourceBranch = (
  source: CalculationSlotSource | undefined,
): ReferenceBranch | null => (source ? SLOT_SOURCE[source].branch : null)

/**
 * The branch a row is named after, where it is named after one rather than
 * after the kind of term it holds.
 *
 * A product and an Arbeitsleistung stay references, so the figure beside the
 * row is that entry's own price and the branch is what the row is. A block or
 * a group can be detached into terms, and those terms name it.
 */
export const namingBranch = (
  source: CalculationSlotSource | undefined,
): ReferenceBranch | null =>
  source && !isDetachable(source) ? sourceBranch(source) : null

/**
 * Whether the row can be turned into terms of its own.
 *
 * Read by the badge, by the menu that offers the detach and by the service
 * that performs it, so the three cannot promise different things.
 */
export const isDetachable = (source: CalculationSlotSource): boolean =>
  SLOT_SOURCE[source].detachable
